import {Injectable, TemplateRef, ViewContainerRef} from '@angular/core';
import {ActionsLayout, DialogAction, DialogRef, DialogService} from '@progress/kendo-angular-dialog';
import {KendoMessageDialogComponent} from '../../web/common/kendo-message-dialog/kendo-message-dialog.component';
import {KendoConfirmationDialogComponent} from '../../web/common/kendo-confirmation-dialog/kendo-confirmation-dialog.component';
import {Observable,lastValueFrom} from 'rxjs';
import {KendoYesNoDialogComponent} from '../../web/common/kendo-yes-no-dialog/kendo-yes-no-dialog.component';
import {map} from 'rxjs/operators';

import {NotificationService, Type} from '@progress/kendo-angular-notification';

@Injectable({
  providedIn: 'root'
})
export class FSInvoiceDialogService {


  public type: Type;


  constructor(private dialogService: DialogService,
              private notificationService: NotificationService) {
  }

  async openDialog(options): Promise<string> {
    const dialog: DialogRef = this.dialogService.open(options as DialogSettings);
    dialog.dialog.location.nativeElement.classList.add(options.type);
    const results = await lastValueFrom(dialog.result).then(response => response as DialogAction);
    return results.text;
  }

  openDialogForDownload(options): Observable<any> {
    const dialog: DialogRef = this.dialogService.open(options as DialogSettings);
    dialog.dialog.location.nativeElement.classList.add(options.type);
    return dialog.result;
  }

  public open(options: DialogSettings): DialogRef {
    return this.dialogService.open(options);
  }

  public showMessageDialog(title: string, message: string) {
    const dialogRef = this.dialogService.open({
      content: KendoMessageDialogComponent
    });

    const messageDialog = dialogRef.content.instance;
    messageDialog.title = title;
    messageDialog.message = message;
  }

  public showErrorMessageDialog(title: string, message: string) {
    // TODO
  }

  public showMessagesDialog(title: string, messages: Array<string>) {
    const dialogRef = this.dialogService.open({
      content: KendoMessageDialogComponent
    });

    const messageDialog = dialogRef.content.instance;
    messageDialog.title = title;
    messageDialog.messages = messages;
  }

  public showConfirmInfoDialog(title: string, message: string, callback: any) {
    const dialogRef = this.dialogService.open({
      content: KendoMessageDialogComponent
    });

    const messageDialog = dialogRef.content.instance;
    messageDialog.title = title;
    messageDialog.message = message;
    dialogRef.result.subscribe(result => {
      if (messageDialog.confirmed) {
        callback();
      }
    });
  }

  public showConfirmationDialog(title: string, question: string, callback: any) {
    const dialogRef = this.dialogService.open({
      content: KendoConfirmationDialogComponent
    });

    const confirmationDialog = dialogRef.content.instance;
    confirmationDialog.title = title;
    confirmationDialog.question = question;
    dialogRef.result.subscribe(result => {
      if (confirmationDialog.selected == 'Yes') {
        callback(true);
      } else {
        callback(false);
      }
    });
  }

  public showBetterYesNoDialog(title: string, question: string): Observable<boolean> {
    const dialogRef = this.dialogService.open({
      content: KendoConfirmationDialogComponent
    });

    const confirmationDialog = dialogRef.content.instance;
    confirmationDialog.title = title;
    confirmationDialog.question = question;
    return dialogRef.result.pipe(
      map(result => confirmationDialog.selected == 'Yes')
    );
  }

  public showConfirmationDialogWithFurtherInfo(title: string,
                                               question: string,
                                               furtherInfo: Array<string>,
                                               yesCallback: any,
                                               noCallback: any) {
    const dialogRef = this.dialogService.open({
      content: KendoConfirmationDialogComponent
    });

    const confirmationDialog = dialogRef.content.instance;
    confirmationDialog.title = title;
    confirmationDialog.question = question;
    confirmationDialog.furtherInfo = furtherInfo;
    dialogRef.result.subscribe(result => {
      if (confirmationDialog.selected == 'Yes') {
        yesCallback();
      } else {
        noCallback();
      }
    });
  }

  public showYesNoDialog(title: string, question: string): Observable<boolean> {
    const dialogRef = this.dialogService.open({
      content: KendoYesNoDialogComponent
    });

    const yesNoDialog = dialogRef.content.instance;
    yesNoDialog.title = title;
    yesNoDialog.question = question;

    return dialogRef.result as Observable<boolean>;
  }

  // public showDefault(): void {
  //   this.type = 'default';
  //
  //   this.notificationService.show({
  //     content: this.notificationTemplate,
  //     hideAfter: 600,
  //     position: {horizontal: 'right', vertical: 'top'},
  //     animation: {type: 'fade', duration: 400},
  //     type: {style: 'none', icon: false}
  //   });
  // }

  public showSuccessNotification(message: string): void {
    this.notificationService.show({
      content: message,
      hideAfter: 1200,
      position: {horizontal: 'center', vertical: 'top'},
      animation: {type: 'fade', duration: 400},
      type: {style: 'success', icon: true}
    });
  }

  public showWarningNotification(message: string): void {
    this.notificationService.show({
      content: message,
      hideAfter: 1200,
      position: {horizontal: 'center', vertical: 'top'},
      animation: {type: 'fade', duration: 400},
      type: {style: 'warning', icon: true}
    });
  }

  public showInfoNotification(message: string): void {
    this.notificationService.show({
      content: message,
      hideAfter: 600,
      position: {horizontal: 'center', vertical: 'top'},
      animation: {type: 'fade', duration: 400},
      type: {style: 'info', icon: true}
    });
  }

  public showErrorNotification(message: string): void {
    this.notificationService.show({
      content: message,
      hideAfter: 900,
      position: {horizontal: 'center', vertical: 'top'},
      animation: {type: 'fade', duration: 800},
      type: {style: 'error', icon: true}
    });
  }

  public showErrorNotificationForDuration(message: string, duration: number): void {
    this.notificationService.show({
      content: message,
      hideAfter: duration,
      position: {horizontal: 'center', vertical: 'top'},
      animation: {type: 'fade', duration: duration},
      type: {style: 'error', icon: true}
    });
  }

}


export declare class DialogSettings {
  /**
   * Sets the title of the Dialog. If `title` is omitted,
   * the Dialog will not render a **Close** button.
   */
  type?: string;
  /**
   * Sets the title of the Dialog. If `title` is omitted,
   * the Dialog will not render a **Close** button.
   */
  title?: string;
  /**
   * Defines the content of the Dialog.
   * ([see example]({% slug service_dialog %}#toc-using-components)).
   */
  content?: string | TemplateRef<any> | Function;
  /**
   * Specifies the width of the Dialog.
   * A numeric value sets the width in pixels.
   * A string value sets the width in arbitrary units&mdash;for example, `50%`.
   */
  width?: number | string;
  /**
   * Specifies the minimum width of the Dialog.
   * A numeric value sets the minimum width in pixels.
   * A string value sets the minimum width in arbitrary units&mdash;for example, `50%`.
   */
  minWidth?: number | string;
  /**
   * Specifies the maximum width of the Dialog.
   * A numeric value sets the maximum width in pixels.
   * A string value sets the maximum width in arbitrary units&mdash;for example, `50%`.
   */
  maxWidth?: number | string;
  /**
   * Specifies the height of the Dialog.
   * A numeric value sets the height in pixels.
   * A string value sets the height in arbitrary units&mdash;for example, `50%`.
   */
  height?: number | string;
  /**
   * Specifies the minimum height of the Dialog.
   * A numeric value sets the minimum height in pixels.
   * A string value sets the minimum height in arbitrary units&mdash;for example, `50%`.
   */
  minHeight?: number | string;
  /**
   * Specifies the maximum height of the Dialog.
   * A numeric value sets the maximum height in pixels.
   * A string value sets the maximum height in arbitrary units&mdash;for example, `50%`.
   */
  maxHeight?: number | string;
  /**
   * Defines the container in which the Dialog will be inserted.
   * Specifying this option changes the place in the page hierarchy where the Dialog will be inserted.
   * The styling of the component will remain the same.
   */
  appendTo?: ViewContainerRef;
  /**
   * Specifies the title of the close button.
   */
  closeTitle?: string;
  /**
   * Sets the action buttons of the Dialog.
   */
  actions?: DialogAction[] | any[] | TemplateRef<any>;
  /**
   * Specifies the layout of the action buttons in the Dialog.
   */
  actionsLayout?: ActionsLayout;
  /**
   * Sets the focused element query selector.
   */
  autoFocusedElement?: string;
}

export enum DialogType {
  INFO = 'infoDialog',
  QUESTION = 'questionDialog',
  WARNING = 'warningDialog',
  ERROR = 'errorDialog'
}
